import { ReactNode, useEffect, useState } from 'react'
import { Tab } from '../../components/Sidebar'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import JobService from '../../services/JobService'
import { isAdmin } from '../../helpers/utils'
import CustomButton from '../../components/Button'
import CustomModal from '../../containers/Modal'
import { JobPostDTO } from '../../models/post'
import JobPost from './JobPost'
import JobPostModal from '../../components/Modal/JobPostModal'
import CustomTabs, { CustomTab } from '../../containers/Tabs'
import CustomPaper from '../../containers/Paper'
import NoResults from '../../containers/NoResults'
import { CircularProgress } from '@mui/material'
import UtilitiesService from '../../services/UtilitiesService'

const buttons: Tab[] = [
    {
        label: 'Crear Oferta Laboral',
        icon: <AddCircleOutlineIcon />,
        id: 1,
    },
]

const JobPage = () => {
    const [activePosts, setActivePosts] = useState<JobPostDTO[]>()
    const [recommendedPosts, setRecommendedPosts] = useState<JobPostDTO[]>()
    const [pendingPosts, setPendingPosts] = useState<JobPostDTO[]>()
    const [isNewPostOpen, setIsNewPostOpen] = useState<boolean>(false)
    const [mutate, setMutate] = useState<boolean>(false)
    const [selectedTab, setSelectedTab] = useState<string>('Ofertas laborales activas')
    const [email, setEmail] = useState<string>('')

    useEffect(() => {
        new UtilitiesService().getAppConfig().then((response) => {
            if (response.status !== 200) return
            setEmail(response.data.email)
        })
    })

    useEffect(() => {
        const jobService = new JobService()
        jobService.getJobPosts().then((response) => {
            if (response.status !== 200) return
            setActivePosts(response.data)
        })
        jobService.getRecommendedPosts().then((response) => {
            if (response.status !== 200) return
            setRecommendedPosts(response.data)
        })
        jobService.getMyJobPosts().then((response) => {
            if (response.status !== 200) return
            setPendingPosts(response.data)
        })
    }, [mutate])

    const jobPostsList = (jobPosts: JobPostDTO[], emptyMessage: ReactNode) => {
        return (
            <div className="flex flex-col w-full gap-2 md:gap-4">
                {!isAdmin() && (
                    <span className="text-xs md:text-base">
                        {`Si tenés alguna oferta laboral que quieras que aparezca acá, podés enviar un correo
                        electrónico a `}
                        <a href={`mailto:${email}`}>{email}</a>
                    </span>
                )}
                {jobPosts.length > 0 ? (
                    <div className="flex flex-col gap-2 md:gap-4">
                        {jobPosts.map((x) => (
                            <CustomPaper key={x.id}>
                                <JobPost
                                    info={x}
                                    setMutate={setMutate}
                                />
                            </CustomPaper>
                        ))}
                    </div>
                ) : (
                    emptyMessage
                )}
            </div>
        )
    }

    const tabs: CustomTab[] = [
        {
            value: 'Ofertas laborales activas',
            label: <div className="flex gap-1 items-center">Ofertas activas</div>,
            content: activePosts ? (
                jobPostsList(
                    activePosts,
                    <CustomPaper>
                        <NoResults
                            title="No hay ofertas laborales activas"
                            subtitle="Mantenete atento, pronto habrá más propuestas"
                        />
                    </CustomPaper>,
                )
            ) : (
                <div className="flex w-full justify-center">
                    <CircularProgress
                        className="m-2 md:m-5 text-primary"
                        size={64}
                    />
                </div>
            ),
            onChange: () => {
                setMutate((prevMutate) => !prevMutate)
            },
        },
        {
            value: 'Ofertas laborales recomendadas',
            label: <div className="flex gap-1 items-center">Ofertas recomendadas</div>,
            content: recommendedPosts ? (
                jobPostsList(
                    recommendedPosts,
                    <CustomPaper>
                        <NoResults
                            title="No hay recomendaciones"
                            subtitle="Pronto aparecerán ofertas que se ajusten a tu perfil"
                        />
                    </CustomPaper>,
                )
            ) : (
                <div className="flex w-full justify-center">
                    <CircularProgress
                        className="m-2 md:m-5 text-primary"
                        size={64}
                    />
                </div>
            ),
            onChange: () => {
                setMutate((prevMutate) => !prevMutate)
            },
        },
        {
            value: 'Solicitudes pendientes',
            label: <div className="flex gap-1 items-center">Solicitudes pendientes</div>,
            content: pendingPosts ? (
                jobPostsList(
                    pendingPosts,
                    <CustomPaper>
                        <NoResults
                            title="No tenés solicitudes pendientes"
                            subtitle="Buscá entre las ofertas disponibles para postularte"
                        />
                    </CustomPaper>,
                )
            ) : (
                <div className="flex w-full justify-center">
                    <CircularProgress
                        className="m-2 md:m-5 text-primary"
                        size={64}
                    />
                </div>
            ),
            onChange: () => {
                setMutate((prevMutate) => !prevMutate)
            },
        },
    ]

    return (
        <div className="flex flex-col gap-2 md:gap-4 w-full">
            {isAdmin() ? (
                <div className="flex flex-col md:gap-4">
                    {buttons.map((tab) => (
                        <CustomButton
                            key={tab.label}
                            text={tab.label}
                            onClick={() => setIsNewPostOpen(true)}
                            className={`w-full h-[36px] bg-secondary text-white`}
                            startIcon={tab.icon}
                            secondary
                        />
                    ))}
                    {activePosts ? (
                        jobPostsList(
                            activePosts,
                            <CustomPaper>
                                <NoResults
                                    title="No hay propuestas laborales"
                                    subtitle="¡Podés crear cuantas quieras!"
                                />
                            </CustomPaper>,
                        )
                    ) : (
                        <div className="flex w-full justify-center">
                            <CircularProgress
                                className="m-2 md:m-5 text-primary"
                                size={64}
                            />
                        </div>
                    )}
                </div>
            ) : (
                <CustomTabs
                    tabs={tabs}
                    value={selectedTab}
                    setValue={setSelectedTab}
                    gap={4}
                    type="buttons"
                    fullWidth
                />
            )}
            <CustomModal
                isOpen={isNewPostOpen}
                handleClose={() => setIsNewPostOpen(false)}
            >
                <JobPostModal
                    handleClose={() => setIsNewPostOpen(false)}
                    setMutate={setMutate}
                />
            </CustomModal>
        </div>
    )
}

export default JobPage
